// What a thing of his looks like, wherever it is drawn: as a picture in his bag's pop-up (components/ui/BagPanel.jsx),
// and the newest of them in the bubble over his head (components/three/npc/ThoughtBubble.jsx).
//
// Three kinds of thing reach his bag, and each is drawn from the model it already has: a found thing from trinkets.glb,
// as on his shelf; a meal from the pieces of buy_items.glb, as on the ground (lib/items/parts.js); and what came out of
// his vending machine from the machine's own model, as in the drop (components/ui/VendingDrop.jsx). A look says which,
// and nothing about how to draw it, so it can be tested without a renderer. Imports nothing but the parts list.

import { DRAWN } from '../items/parts.js'

export const TRINKET_MODEL = '/models/trinkets.glb'
export const VEND_MODEL = '/models/vend_items.glb'

// What the town hands him out of the machine (sim-server world/VendingMachine.js handItem), by its item id, as the node
// that thing is in vend_items.glb. The rest of the machine's rows are eaten or used on the spot and never reach the bag.
const VEND_NODE = { noodles: 'noodles', sprays: 'spray', camera: 'camera' }
// The machine holds four disks, a colour a column; a disk he has carries its number and not its column, so the number
// picks the colour, the same one every time.
const DISKS = ['disk_blue', 'disk_black', 'disk_grey', 'disk_red']

/** The look of one of his items: { buy, spec } for a meal, { vend } for a thing out of the machine, null for neither. */
export function itemLook(itemId, spec = null) {
  const id = String(itemId || '')
  if (DRAWN.includes(id)) return { buy: id, spec: spec && typeof spec === 'object' ? spec : {} }
  if (VEND_NODE[id]) return { vend: VEND_NODE[id] }
  if (id === 'disk') return { vend: DISKS[Math.abs(Math.floor(Number(spec?.disk) || 0)) % DISKS.length] }
  return null
}

/** The look of an entry of /api/bag: { kind: 'trinket', trinketKind, colour } or { kind: 'item', itemId, spec }. */
export function lookOfEntry(entry) {
  if (!entry) return null
  if (entry.kind === 'trinket') return { trinket: String(entry.trinketKind || ''), colour: entry.colour || null }
  if (entry.kind === 'item') return itemLook(entry.itemId, entry.spec)
  return null
}

/** The look of what his broadcast says he carries: { kind, colour } for a found thing, { itemId, spec } for an item. */
export function lookOfCarrying(carrying) {
  if (!carrying) return null
  if (carrying.itemId) return itemLook(carrying.itemId, carrying.spec)
  if (carrying.kind) return { trinket: String(carrying.kind), colour: carrying.colour || null }
  return null
}

// A spec as text that is the same whatever order its keys arrived in, so two broadcasts of one doughnut are one key.
function steady(v) {
  if (Array.isArray(v)) return `[${v.map(steady).join(',')}]`
  if (v && typeof v === 'object') return `{${Object.keys(v).sort().map((k) => `${k}:${steady(v[k])}`).join(',')}}`
  return JSON.stringify(v ?? null)
}

/** One string a look: what a picture is kept under, and how one thing over his head is told from the next. */
export function lookKey(look) {
  if (!look) return ''
  if (look.trinket !== undefined) return `trinket:${look.trinket}:${look.colour || ''}`
  if (look.vend) return `vend:${look.vend}`
  if (look.buy) return `buy:${look.buy}:${steady(look.spec || {})}`
  return ''
}
